// ----------------------------------------------------------------------------
// Component: Accordion
// ----------------------------------------------------------------------------
//
// Generic accordion component.
//
// Requires the following structure:
// .js-accordion__item
//     .js-accordion__button
//     .js-accordion__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './accordion.scss';

// -------------------------------------
// Variables
// -------------------------------------

var ACCORDION = '.js-accordion';
var ACCORDION_BUTTON = '.js-accordion__button';
var ACCORDION_ITEM = '.js-accordion__item';
var ACCORDION_CONTENT = '.js-accordion__content';
var ACCORDION_TITLE = '.accordion__title';
var ACCORDION_CONTENT_MOBILE = '.js-accordion__content--mobile-only';

// Constant trigger classes
var OPEN_CLASS = 'is-open';

// -------------------------------------
// Helper Functions
// -------------------------------------

function setTabIndex(e, value) {
  e.setAttribute('tabindex', value);
}
function toggleAccordion(accordion) {
  var scope = accordion.container.hasAttribute('data-collapse-all') ? accordion.container : accordion.item.parentElement;
  scope.querySelectorAll('.' + OPEN_CLASS).forEach(function (e) {
    e.classList.remove(OPEN_CLASS);
  });
  scope.querySelectorAll(ACCORDION_CONTENT).forEach(function (e) {
    e.hidden = true;
    e.style.height = null;
  });
  scope.querySelectorAll(ACCORDION_BUTTON).forEach(function (e) {
    e.setAttribute('aria-expanded', 'false');
  });
  scope.querySelectorAll(ACCORDION_CONTENT).forEach(function (e) {
    e.querySelectorAll('a').forEach(el => {
      setTabIndex(el, -1);
    });
  });
  if (accordion.isOpening) {
    accordion.button.classList.add(OPEN_CLASS);
    accordion.item.classList.add(OPEN_CLASS);
    accordion.content.classList.add(OPEN_CLASS);
    accordion.content.style.height = accordion.content.scrollHeight + 'px';
    accordion.button.setAttribute('aria-expanded', 'true');
    accordion.content.hidden = false;
    accordion.content.querySelectorAll('a').forEach(function (el) {
      setTabIndex(el, 0);
    });
  }
}
function accordionEventHandle(e, button) {
  var accordion = {
    button: button,
    item: button.closest(ACCORDION_ITEM),
    title: button.closest(ACCORDION_TITLE),
    content: button.closest(ACCORDION_ITEM).querySelector(ACCORDION_CONTENT),
    isOpening: !button.classList.contains(OPEN_CLASS),
    container: button.closest(ACCORDION),
    button_group: button.closest(ACCORDION).querySelectorAll(ACCORDION_BUTTON)
  };
  if (e.type === 'click') {
    e.preventDefault();
    toggleAccordion(accordion);
    if (accordion.isOpening && accordion.container.classList.contains('product-accordion')) {
      setTimeout(function () {
        var itemTop = accordion.item.getBoundingClientRect().top;
        if (itemTop < 0) {
          window.scrollTo({
            top: accordion.item.getBoundingClientRect().top + window.scrollY - 85,
            behavior: 'smooth'
          });
        }
      }, 250);
    }
  } else if (e.type === 'keydown') {
    var key = e.which.toString();
    if (key.match(/35|36|38|40/)) {
      e.preventDefault();
      var index = Array.prototype.indexOf.call(accordion.button_group, button);
      var length = accordion.button_group.length;
      var newIndex;
      switch (key) {
        case '38':
          // Up
          newIndex = (index + length + -1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '40':
          // Down
          newIndex = (index + length + 1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '35':
          // End
          accordion.button_group[length - 1].focus();
          break;
        case '36':
          // Home
          accordion.button_group[0].focus();
          break;
      }
    }
  }
}
function initAccordionTabIndex() {
  var accordionContentLinks;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
    accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(.").concat(OPEN_CLASS, ") a"));
  } else {
    accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(.").concat(OPEN_CLASS, "):not(").concat(ACCORDION_CONTENT_MOBILE, ") a"));
  }
  accordionContentLinks.forEach(function (el) {
    setTabIndex(el, -1);
  });
}

/**
 * PDP: open the first accordion item by default (product-accordion only).
 */
function openFirstProductAccordion() {
  document.querySelectorAll('.product-accordion.js-accordion').forEach(function (container) {
    var firstItem = container.querySelector(ACCORDION_ITEM);
    var firstBtn = firstItem && firstItem.querySelector(ACCORDION_BUTTON);
    if (!firstBtn || firstBtn.classList.contains(OPEN_CLASS)) return;
    accordionEventHandle({
      type: 'click',
      preventDefault: () => {}
    }, firstBtn);
  });
}
function initAccordions() {
  openFirstProductAccordion();
  initAccordionTabIndex();
}

// -------------------------------------
// Hooks (event delegation so accordions work after section re-render)
// -------------------------------------

document.addEventListener('click', function (e) {
  if (!(e.target instanceof Element)) return;
  var button = e.target.closest(ACCORDION_BUTTON);
  if (!button) return;
  accordionEventHandle(e, button);
});
document.addEventListener('keydown', function (e) {
  if (!(e.target instanceof Element)) return;
  var button = e.target.closest(ACCORDION_BUTTON);
  if (!button) return;
  accordionEventHandle(e, button);
});
document.addEventListener('DOMContentLoaded', initAccordions);
document.addEventListener('shopify:section:load', initAccordions);
window.addEventListener('resize', function () {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
    // On mobile, find all accordion content links of the close accordions to add tabindex -1
    var accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(.").concat(OPEN_CLASS, ") a"));
    accordionContentLinks.forEach(function (e) {
      setTabIndex(e, -1);
    });
  } else {
    // If on desktop, find all accordion content links that belongs to the #accordion-cart--content on the cart page to remove tabindex -1.
    var accordionCartContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT).concat(ACCORDION_CONTENT_MOBILE, " a"));
    accordionCartContentLinks.forEach(function (e) {
      setTabIndex(e, 0);
    });
  }

  // Re-adjust all the expanded accordion content tab height on "resize" event by setting their height to "auto"
  var expandedAccordionContent = document.querySelectorAll("".concat(ACCORDION_CONTENT, ".").concat(OPEN_CLASS));
  expandedAccordionContent.forEach(function (accordionContent) {
    accordionContent.style.height = 'auto';
  });
});